import { Routes } from '@angular/router'

import { authGuard } from './guards/auth.guard'
import { groupGuard } from './guards/group.guard'
import { loginGuard } from './guards/login.guard'
import { ContentLayoutBctComponent } from './layouts/content-layout-bct/content-layout-bct.component'
import { ContentLayoutComponent } from './layouts/content-layout/content-layout.component'
import { LoginLayoutComponent } from './layouts/login-layout/login-layout.component'
import { SubLayoutComponent } from './layouts/sub-layout/sub-layout.component'
import { UserGroup } from './models/user.model'
import { AddressHistoryComponent } from './pages/address-history/address-history.component'
import { AddressMasterComponent } from './pages/address-master/address-master.component'
import { ChangePasswordComponent } from './pages/change-password/change-password.component'
import { ClearingDetailComponent } from './pages/clearing-detail/clearing-detail.component'
import { ClearingComponent } from './pages/clearing/clearing.component'
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component'
import { LoginComponent } from './pages/login/login.component'
import { SnapshotDetailComponent } from './pages/snapshot-detail/snapshot-detail.component'
import { SnapshotsComponent } from './pages/snapshots/snapshots.component'
import { StartComponent } from './pages/start/start.component'
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component'
import {
  ConflictDetailComponent,
  ViewMode as ConflictDetailViewMode,
} from './pages/conflict-detail/conflict-detail.component'
import { UploadDetailComponent } from './pages/upload-detail/upload-detail.component'
import { UploadFileComponent } from './pages/upload-file/upload-file.component'
import { UploadComponent } from './pages/upload/upload.component'

export type RouteData<T = string> = {
  breadcrumbI18nKey: string
  groups?: UserGroup[]
  viewMode?: T
}

export const routes: Routes = [
  {
    path: 'login',
    component: LoginLayoutComponent,
    children: [
      {
        path: '',
        component: LoginComponent,
      },
    ],
    canActivate: [loginGuard],
  },
  {
    path: 'forgot-password',
    component: LoginLayoutComponent,
    children: [
      {
        path: '',
        component: ForgotPasswordComponent,
      },
    ],
  },
  {
    path: 'password-change',
    component: LoginLayoutComponent,
    children: [
      {
        path: '',
        component: ChangePasswordComponent,
      },
    ],
  },
  {
    path: '',
    component: ContentLayoutComponent,
    children: [
      {
        path: '',
        component: StartComponent,
        pathMatch: 'full',
      },
    ],
    canActivate: [authGuard],
  },
  {
    path: '',
    component: ContentLayoutBctComponent,
    children: [
      {
        path: 'unauthorized',
        component: UnauthorizedComponent,
        data: {
          breadcrumbI18nKey: 'pages.unauthorized.title',
        } satisfies RouteData,
      },
      {
        path: 'addresses',
        component: AddressMasterComponent,
        canActivate: [groupGuard],
        data: {
          breadcrumbI18nKey: 'pages.addressMaster.title',
          groups: ['snapshot'],
        } satisfies RouteData,
      },
      {
        path: 'addresses',
        component: SubLayoutComponent,
        canActivate: [groupGuard],
        data: {
          breadcrumbI18nKey: 'pages.addressMaster.title',
          groups: ['snapshot'],
        } satisfies RouteData,
        children: [
          {
            path: ':id',
            component: AddressHistoryComponent,
            canActivate: [groupGuard],
            data: {
              breadcrumbI18nKey: 'pages.addressHistory.title',
              groups: ['snapshot'],
            } satisfies RouteData,
          },
        ],
      },
      {
        path: 'snapshots',
        pathMatch: 'full',
        component: SnapshotsComponent,
        canActivate: [groupGuard],
        data: {
          breadcrumbI18nKey: 'pages.snapshots.title',
          groups: ['snapshot'],
        } satisfies RouteData,
      },
      {
        path: 'snapshots',
        component: SubLayoutComponent,
        canActivate: [groupGuard],
        data: {
          breadcrumbI18nKey: 'pages.snapshots.title',
          groups: ['asset'],
        } satisfies RouteData,
        children: [
          {
            path: 'snapshot-detail/:id',
            component: SnapshotDetailComponent,
            canActivate: [groupGuard],
            data: {
              breadcrumbI18nKey: 'pages.snapshotDetail.title',
              groups: ['asset'],
            } satisfies RouteData,
          },
        ],
      },
      {
        path: 'uploads',
        pathMatch: 'full',
        component: UploadComponent,
        canActivate: [groupGuard],
        data: {
          breadcrumbI18nKey: 'pages.uploads.title',
          groups: ['asset'],
        } satisfies RouteData,
      },
      {
        path: 'uploads',
        component: SubLayoutComponent,
        canActivate: [groupGuard],
        data: {
          breadcrumbI18nKey: 'pages.uploads.title',
          groups: ['asset'],
        } satisfies RouteData,
        children: [
          {
            path: 'upload-file',
            component: UploadFileComponent,
            canActivate: [groupGuard],
            data: {
              breadcrumbI18nKey: 'pages.uploadFile.title',
              groups: ['asset'],
            } satisfies RouteData,
          },
          {
            path: 'upload-detail/:id',
            component: UploadDetailComponent,
            canActivate: [groupGuard],
            data: {
              breadcrumbI18nKey: 'pages.uploadDetail.title',
              groups: ['asset'],
            } satisfies RouteData,
          },
          {
            path: 'upload-detail/:id',
            component: SubLayoutComponent,
            canActivate: [groupGuard],
            data: {
              breadcrumbI18nKey: 'pages.uploadDetail.title',
              groups: ['asset'],
            } satisfies RouteData,
            children: [
              {
                path: 'conflict-detail/:id',
                component: ConflictDetailComponent,
                canActivate: [groupGuard],
                data: {
                  breadcrumbI18nKey: 'pages.conflictDetail.title',
                  groups: ['asset'],
                  viewMode: 'upload',
                } satisfies RouteData<ConflictDetailViewMode>,
              },
            ],
          },
        ],
      },

      {
        path: 'clearings',
        component: ClearingComponent,
        pathMatch: 'full',
        canActivate: [groupGuard],
        data: {
          breadcrumbI18nKey: 'pages.clearings.title',
          groups: ['audit'],
        } satisfies RouteData,
      },
      {
        path: 'clearings',
        component: SubLayoutComponent,
        canActivate: [groupGuard],
        data: {
          breadcrumbI18nKey: 'pages.clearings.title',
          groups: ['audit'],
        } satisfies RouteData,
        children: [
          {
            path: 'clearing-detail/:id',
            component: ClearingDetailComponent,
            // pathMatch: 'full',
            canActivate: [groupGuard],
            data: {
              breadcrumbI18nKey: 'pages.clearingDetail.title',
              groups: ['audit'],
            } satisfies RouteData,
            children: [],
          },

          {
            path: 'clearing-detail/:id',
            component: SubLayoutComponent,
            canActivate: [groupGuard],
            data: {
              breadcrumbI18nKey: 'pages.clearingDetail.title',
              groups: ['audit'],
            } satisfies RouteData,
            children: [
              {
                path: 'conflict-detail/:id',
                component: ConflictDetailComponent,
                canActivate: [groupGuard],
                data: {
                  breadcrumbI18nKey: 'pages.conflictDetail.title',
                  groups: ['audit'],
                  viewMode: 'clearing',
                } satisfies RouteData<ConflictDetailViewMode>,
              },
            ],
          },
        ],
      },
    ],
    canActivate: [authGuard],
  },
  // { path: '**', component: PageNotFoundComponent },  // Wildcard route for a 404
]
